import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";

const HOUSING = { apartment: "Appartement", house: "Maison", shared: "Colocation", other: "Autre" };
const DEFAULT_ROOMS = ["Cuisine", "Salon", "Chambre", "Salle de bain", "Bureau", "Garage", "Cellier", "Extérieur", "Autre"];
const F = ({ id, label, children }) => <div><Label htmlFor={id}>{label}</Label><div className="mt-1.5">{children}</div></div>;

const RoomsEditor = ({ rooms, onChange, disabled }) => {
  const [n, setN] = useState("");
  const add = () => { const r = n.trim(); if (r && !rooms.includes(r)) onChange([...rooms, r]); setN(""); };
  return (
    <div className="space-y-2" data-testid="settings-rooms">
      <div className="flex flex-wrap gap-1.5">
        {rooms.map((r) => (
          <span key={r} className="tone-chip tone-sky inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium" data-testid={`settings-room-${r}`}>
            {r}{!disabled && <button type="button" aria-label={`Retirer ${r}`} onClick={() => onChange(rooms.filter((x) => x !== r))} data-testid={`settings-room-remove-${r}`}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input value={n} onChange={(e) => setN(e.target.value)} maxLength={40} placeholder="Ex. Buanderie, Chambre d'Emma…" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid="settings-room-input" />
          <Button type="button" variant="outline" className="rounded-full" onClick={add} data-testid="settings-room-add-button"><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
        </div>
      )}
    </div>
  );
};

const SettingsForm = ({ h, canManage }) => {
  const qc = useQueryClient();
  const { reload } = useAuth();
  const [v, setV] = useState({ name: h.name, housing_type: h.housing_type || "", address: h.address || "", surface_m2: h.surface_m2 ?? "", rooms: h.rooms || DEFAULT_ROOMS });
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/households/${h.id}`, { name: v.name, housing_type: v.housing_type || null, address: v.address || null,
        surface_m2: v.surface_m2 === "" ? null : Number(v.surface_m2), rooms: v.rooms });
      ["household", "rooms", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      await reload();
      toast.success("Paramètres du foyer enregistrés");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="card-surface space-y-5 p-5 sm:p-6" data-testid="household-settings-form">
      <fieldset disabled={!canManage} className="grid gap-4 sm:grid-cols-2">
        <F id="h-name" label="Nom du foyer"><Input id="h-name" value={v.name} onChange={set("name")} required minLength={2} maxLength={80} data-testid="settings-name-input" /></F>
        <F id="h-type" label="Type de logement">
          <Select value={v.housing_type} onValueChange={set("housing_type")} disabled={!canManage}>
            <SelectTrigger id="h-type" data-testid="settings-housing-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{Object.entries(HOUSING).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F id="h-addr" label="Adresse"><Input id="h-addr" value={v.address} onChange={set("address")} maxLength={200} placeholder="N°, rue, code postal, ville" data-testid="settings-address-input" /></F>
        <F id="h-surf" label="Surface (m²)"><Input id="h-surf" type="number" min="0" step="1" value={v.surface_m2} onChange={set("surface_m2")} data-testid="settings-surface-input" /></F>
      </fieldset>
      <div>
        <Label>Pièces du logement</Label>
        <p className="mb-2 text-xs text-muted-foreground">Elles servent à ranger vos équipements et à guider la détection par photo.</p>
        <RoomsEditor rooms={v.rooms} onChange={(rooms) => setV({ ...v, rooms })} disabled={!canManage} />
      </div>
      {canManage ? <Button type="submit" className="rounded-full" data-testid="settings-save-button">Enregistrer</Button>
        : <p className="text-xs text-muted-foreground" data-testid="settings-readonly-note">Seuls les propriétaires et administrateurs du foyer peuvent modifier ces paramètres.</p>}
    </form>
  );
};

export const HouseholdSettingsPanel = () => {
  const { data, canManage } = useHouseholdDetail();
  if (!data) return <Skeleton className="h-64 rounded-2xl" />;
  return <SettingsForm key={data.id} h={data} canManage={canManage} />;
};
